/* Flags that are requested via the featureFlags API, must be allow-listed prior to use.
 * Flag access is controlled via the `authorized-flag-access` Launch Darkly flag.
 * To add an authorized flag, modify the Production variation
 * (https://app.launchdarkly.com/chronosphere-cloud/rc/features/authorized-flag-access/variations)
 * to include the slug of your newly added flag.
 */ // These are flags we want to keep around because they control critical aspects of the application
const PERMANANT_FLAGS = {
    clientSideTracing: {
        key: 'client-side-tracing',
        value: false
    },
    isInternalTenant: {
        key: 'is-internal-tenant',
        value: false
    }
};
// These flags are primarily used for sales team enablement
const DEMO_FLAGS = {};
// Semi-permanent flags that control product-level features (events, traces, etc)
const ENTITLEMENT_FLAGS = {
    chronosphereUniversityLinkEnabled: {
        key: 'chronosphere-university-link-enabled',
        value: false
    },
    docsSiteMode: {
        key: 'docs-site-mode',
        value: 'disabled'
    },
    enableSCIM: {
        key: 'enable-scim',
        value: false
    },
    enableTimelineUI: {
        key: 'enable-timeline-ui',
        value: false
    },
    loggingUI: {
        key: 'logging-ui',
        value: false
    },
    logscaleEnabled: {
        key: 'logscale-enabled',
        value: false
    }
};
// There features span across the entire app
const GLOBAL_FEATURE_FLAGS = {
    enableTimezoneSelection: {
        key: 'timezone-preferences-enabled',
        value: false
    },
    enableHeaderCopyUrlButton: {
        key: 'enable-header-copy-url-button',
        value: false
    },
    enableRemoveContainerOverflow: {
        key: 'enable-remove-container-overflow',
        value: false
    }
};
// DRI: Responder
const SERVICE_FLAGS = {
    enableServiceLinks: {
        key: 'enable-service-links',
        value: false
    },
    serviceConfigv2: {
        key: 'service-config-v2',
        value: false
    },
    eventServiceIntegration: {
        key: 'event-lens-integration-state',
        value: 'off'
    },
    metricsExplorerPinnedScopeEnabled: {
        key: 'pinned-scope.native-metrics-explorer',
        value: 'disabled'
    },
    metricsDdxPoc: {
        key: 'metrics-ddx-poc',
        value: false
    },
    enableBulkOwnershipUpdate: {
        key: 'enable-bulk-ownership-update',
        value: false
    }
};
// DRI: Error budgets
const SLO_FLAGS = {
    enableSloSignals: {
        key: 'enable-slo-signals',
        value: false
    },
    enableSloUi: {
        key: 'enable-slo-ui',
        value: false
    },
    sloMvpFeature: {
        key: 'slo-mvp-feature',
        value: false
    }
};
// DRI: Control
const DROP_RULES_FLAGS = {
    conditionalDropRuleCreationAllowed: {
        key: 'conditional-drop-rule-creation-allowed',
        value: false
    }
};
// DRI: Control
const AGG_RULES_FLAGS = {
    aggRulesRework: {
        key: 'agg-rules-rework',
        value: false
    }
};
// DRI: Control
const METRICS_USAGE_FLAGS = {
    aggregatorLicenseByMatchedWrite: {
        key: 'aggregator-license-by-matched-writes',
        value: false
    },
    usageDetailsExecutionTruncationUI: {
        key: 'usage-details-execution-truncation-ui',
        value: false
    }
};
// DRI: Control
const METRICS_ALLOCATION_FLAGS = {
    enablePoolsUI: {
        key: 'pool-priority-ui',
        value: false
    },
    fetchPoolStatsIndividually: {
        key: 'fetch-poolstats-individually',
        value: false
    },
    enableCreatePool: {
        key: 'enable-create-pool',
        value: false
    },
    metricsQuotasRework: {
        key: 'metrics-quotas-rework',
        value: false
    },
    disableQuotaConfiguration: {
        key: 'disable-quota-configuration',
        value: false
    },
    allowFixedValuesAllocation: {
        key: 'allow-resource-pools-fixed-values-allocation',
        value: false
    }
};
// DRI: Logging
const MONITORS_FLAGS = {
    newMonitorResolver: {
        key: 'enable-new-graphql-monitor-resolver',
        value: false
    },
    enableNotificationRoutesGroupBy: {
        key: 'enable-notification-routes-group-by',
        value: false
    },
    logscaleDatasourceEnabled: {
        key: 'logscale-datasource-enabled',
        value: false
    },
    enableSeriesLabelRouting: {
        key: 'enable-series-label-routing',
        value: false
    }
};
// DRI: Dashboarding
const NATIVE_DASHBOARD_FLAGS = {
    enableDefaultNativeDashboardPreview: {
        key: 'enable-default-native-dashboard-preview',
        value: false
    },
    enableNativeDashboards: {
        key: 'enable-native-dashboards',
        value: 'disabled'
    },
    enableVizFrameworkFormulas: {
        key: 'enable-vf-formulas',
        value: false
    },
    nativeDashboardsDatapointsLimit: {
        key: 'native-dashboards-datapoints-limit',
        value: 400000
    },
    nativeDashboardsSeriesLimit: {
        key: 'native-dashboards-series-limit',
        value: 4000
    },
    showNativeDashboardPreview: {
        key: 'show-native-dashboard-preview',
        value: false
    },
    enableDashboardSpecMigrationAutoSave: {
        key: 'enable-dashboard-spec-migration-auto-save',
        value: true
    },
    enableLoadClassicDashboardButton: {
        key: 'enable-load-classic-dashboard-button',
        value: true
    },
    enableNativeMetricsExplorerMode: {
        key: 'enable-native-metrics-explorer-mode',
        value: 'off'
    },
    enableNativeDashboardPinnedScopes: {
        key: 'pinned-scope.native-dashboards',
        value: false
    }
};
// DRI: Tracing
const TRACING_FLAGS = {
    tracingErrorCorrelation: {
        key: 'tracing-error-correlation',
        value: false
    },
    tracingTopologyViewEdges: {
        key: 'tracing-topology-view-edges',
        value: false
    },
    tracingTagCharts: {
        key: 'tracing-tag-charts',
        value: false
    },
    tracingTriageUi: {
        key: 'tracing-triage-ui',
        value: false
    },
    tracingUserJourneys: {
        key: 'tracing-user-journeys',
        value: false
    },
    tracingDdxSampleCount: {
        key: 'tracing-ddx-sample-count',
        value: 16
    },
    tracingDashboardUi: {
        key: 'tracing-dashboard-ui',
        value: false
    },
    tracingSystemLimitMetric: {
        key: 'tracing-system-limit-metric',
        value: 'off'
    },
    tracingTagPartitionConcurrency: {
        key: 'tracing-tag-query-partition-concurrency',
        value: 1
    },
    tracingCustomBehaviors: {
        key: 'tracing-custom-behaviors',
        value: false
    },
    tracingPreviewBehavior: {
        key: 'tracing-preview-behavior',
        value: false
    },
    tracingTrendMap: {
        key: 'tracing-trend-map',
        value: false
    }
};
// DRI: Timeline
const TIMELINE_FLAGS = {
    timelineEnableRecentFilters: {
        key: 'enable-recent-filters',
        value: false
    },
    timelineEnableSavedFilters: {
        key: 'enable-saved-filters',
        value: false
    }
};
// DRI: Logging
const LOGGING_FLAGS = {
    loggingEnableConsolidatedExplorer: {
        key: 'enable-consolidated-explorer',
        value: false
    },
    loggingEnableV4QueryGrammar: {
        key: 'enable-logging-ui-query-grammar-v-4',
        value: false
    },
    loggingEnableProgressiveLoadingApis: {
        key: 'enable-progressive-loading-apis',
        value: false
    }
};
const SERVICE_ACCOUNT_FLAGS = {
    // DRI: zach@
    enableServiceTokenUsageCustomerUi: {
        key: 'enable-service-token-usage-customer-ui',
        value: false
    }
};
const INFERENCE_FLAGS = {
    // DRI: shreyas@
    enableInferenceEngine: {
        key: 'enable-inference-engine',
        value: false
    }
};
// DRI: Viz Framework
const VIZ_FRAMEWORK_FLAGS = {
    enableVizFrameworkMocks: {
        key: 'enable-viz-framework-mocks',
        value: false
    },
    enableLinearLoadingBar: {
        key: 'enable-linear-loading-bar',
        value: false
    }
};
/**
 * Configuration for all feature flags fetched from the GQL API.
 */ const FEATURE_FLAG_CONFIG = {
    ...PERMANANT_FLAGS,
    ...DEMO_FLAGS,
    ...GLOBAL_FEATURE_FLAGS,
    ...ENTITLEMENT_FLAGS,
    /*** Workstream flags ***/ ...SERVICE_FLAGS,
    ...SLO_FLAGS,
    ...DROP_RULES_FLAGS,
    ...METRICS_USAGE_FLAGS,
    ...METRICS_ALLOCATION_FLAGS,
    ...MONITORS_FLAGS,
    ...NATIVE_DASHBOARD_FLAGS,
    ...TRACING_FLAGS,
    ...TIMELINE_FLAGS,
    ...LOGGING_FLAGS,
    ...AGG_RULES_FLAGS,
    ...SERVICE_ACCOUNT_FLAGS,
    ...INFERENCE_FLAGS,
    ...VIZ_FRAMEWORK_FLAGS
};
/**
 * These flags are local to the client and are not controlled by LaunchDarkly
 * They are used to control features that are not yet ready for production.
 * All tenants will have the same value for these flags
 * eg. This config is the source of truth for the flag
 */ const LOCAL_FEATURE_FLAGS = {
    enableNewMonitorsGraph: false,
    enableVizFrameworkSchemaValidation: false,
    shouldAutoMigrateGrafanaDashboard: false,
    disableQueriesOutsideOfView: false
};
/**
 * Returns true if the flag is a local feature flag
 */ const isLocalFeatureFlag = (flag)=>flag in LOCAL_FEATURE_FLAGS;

export { FEATURE_FLAG_CONFIG, LOCAL_FEATURE_FLAGS, isLocalFeatureFlag };
//# sourceMappingURL=feature-flags-helper.js.map
